import { useEffect, useState, useRef } from "react";
import { Bell, ArrowRight, X } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Bell icon in the top toolbar. Polls /api/alerts every 30s.
 * Click → dropdown of recent alerts; each alert links to /app/synergy?key=...
 * Alerts are created in the background when a new firm is added (passive watchlist scan).
 */
export default function AlertsBell() {
    const [alerts, setAlerts] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);
    const [stats, setStats] = useState(null); // {counts: {casual, direct}, total_picks, winning_tone}
    const ref = useRef(null);

    const load = async () => {
        try {
            const { data } = await api.get("/alerts");
            setAlerts(data.alerts || []);
            setUnread(data.unread || 0);
        } catch { /* silent — not critical */ }
        try {
            const { data: s } = await api.get("/alerts/reengage-stats");
            setStats(s);
        } catch { /* silent */ }
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        const onDocClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    const markAllRead = async () => {
        await api.post("/alerts/read-all");
        setUnread(0);
        setAlerts((a) => a.map((x) => ({ ...x, read: true })));
    };

    const tieredTone = (score) => {
        if (score >= 90) return "text-emerald-400 border-emerald-500/40 bg-emerald-500/10";
        if (score >= 80) return "text-blue-400 border-blue-500/40 bg-blue-500/10";
        return "text-amber-400 border-amber-500/40 bg-amber-500/10";
    };

    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((x) => !x)}
                data-testid="alerts-bell"
                title="Watchlist alerts"
                className="relative inline-flex items-center justify-center border border-zinc-800 hover:border-amber-500 text-zinc-400 hover:text-amber-500 px-2.5 py-1.5 transition-colors"
            >
                <Bell className="w-3.5 h-3.5" />
                {unread > 0 && (
                    <span data-testid="alerts-badge" className="absolute -top-1.5 -right-1.5 bg-amber-500 text-black font-mono text-[9px] font-black rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>

            {open && (
                <div data-testid="alerts-dropdown" className="absolute right-0 top-full mt-1 w-96 bg-zinc-950 border border-zinc-800 shadow-xl z-50 max-h-[70vh] overflow-y-auto">
                    <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500">Watchlist alerts</div>
                        <div className="flex items-center gap-2">
                            {unread > 0 && (
                                <button onClick={markAllRead} data-testid="mark-all-read" className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 hover:text-amber-500">Mark all read</button>
                            )}
                            <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-zinc-100"><X className="w-3.5 h-3.5" /></button>
                        </div>
                    </div>
                    {stats && stats.total_picks > 0 && (
                        <div className="px-4 py-2 border-b border-zinc-800/60 bg-zinc-900/30" data-testid="reengage-stats">
                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Your A/B follow-ups</div>
                            <div className="flex items-center gap-3 font-mono text-[10px]">
                                <span className="text-zinc-300" data-testid="stats-casual">casual <span className="text-amber-400">{stats.counts?.casual || 0}</span></span>
                                <span className="text-zinc-700">·</span>
                                <span className="text-zinc-300" data-testid="stats-direct">direct <span className="text-amber-400">{stats.counts?.direct || 0}</span></span>
                                {stats.winning_tone && (
                                    <span className="ml-auto text-emerald-400" data-testid="stats-winner">winning: {stats.winning_tone}</span>
                                )}
                            </div>
                        </div>
                    )}
                    {alerts.length === 0 ? (
                        <div className="px-4 py-8 text-center font-mono text-[10px] text-zinc-600">
                            No alerts yet. Add a firm — we'll auto-scan it against your library and flag any 75+ fits.
                        </div>
                    ) : (
                        <div>
                            {alerts.map((a) => {
                                // Re-open alert (someone re-visited a shared pitch/firm link)
                                if (a.kind === "reopen") {
                                    return (
                                        <ReopenAlertCard
                                            key={a.id}
                                            a={a}
                                            onRead={() => { api.post(`/alerts/${a.id}/read`).catch(() => {}); setAlerts((prev) => prev.map((x) => x.id === a.id ? { ...x, read: true } : x)); }}
                                            onRefresh={load}
                                        />
                                    );
                                }
                                // Default: watchlist / synergy alert
                                const key = `${a.firm_a_id}:${a.firm_b_id}:${a.product_id}`;
                                return (
                                    <Link
                                        to={`/app/synergy?key=${encodeURIComponent(key)}`}
                                        key={a.id}
                                        onClick={() => { setOpen(false); api.post(`/alerts/${a.id}/read`).catch(() => {}); }}
                                        data-testid={`alert-${a.id}`}
                                        className={`block px-4 py-3 border-b border-zinc-800/60 hover:bg-zinc-900/40 ${a.read ? "" : "bg-zinc-900/20"}`}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm text-zinc-50 font-semibold truncate">
                                                    {a.firm_a_name} → {a.firm_b_name}
                                                </div>
                                                <div className="font-mono text-[10px] text-amber-400 mt-0.5">{a.product_name || "—"}</div>
                                                <div className="text-xs text-zinc-400 leading-snug mt-1 line-clamp-2">{a.rationale}</div>
                                            </div>
                                            <span className={`inline-block border px-1.5 py-0.5 font-mono text-[10px] shrink-0 ${tieredTone(a.fit_score)}`}>{a.fit_score}</span>
                                        </div>
                                        <div className="mt-1.5 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-zinc-600 hover:text-amber-500">
                                            Open synergy <ArrowRight className="w-2.5 h-2.5" />
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// Re-open alert card — expandable; shows AI-drafted re-engagement email VARIANTS (A/B) when ready.
function ReopenAlertCard({ a, onRead, onRefresh }) {
    const [expanded, setExpanded] = useState(false);
    const [copied, setCopied] = useState(""); // "subject" | "body" | "all" | ""
    const [regenerating, setRegenerating] = useState(false);
    const [activeTone, setActiveTone] = useState("casual");
    // Decision flow: "undecided" (default) | "yes" (editing + ready to send) | "no" (skipped)
    const [decision, setDecision] = useState("undecided");
    // Local edits — initialized from variant when user picks "yes"; preserved per variant.
    const [editedSubject, setEditedSubject] = useState("");
    const [editedBody, setEditedBody] = useState("");

    const isFirm = a.share_kind === "firm";
    const variants = a.email_variants || [];
    const status = a.email_status || "drafting";
    const chosen = a.chosen_variant;

    const variant = variants.find((v) => v.tone === activeTone) || variants[0];

    // When user switches variant tabs OR clicks "Yes, send", seed the editor with the variant content
    const seedEditor = (v) => {
        if (!v) return;
        setEditedSubject(v.subject || "");
        setEditedBody(v.body || "");
    };

    const onSwitchTone = (tone) => {
        setActiveTone(tone);
        if (decision === "yes") {
            const v = variants.find((x) => x.tone === tone);
            seedEditor(v);
        }
    };

    const onYes = () => {
        seedEditor(variant);
        setDecision("yes");
    };

    const onNo = () => setDecision("no");

    const onExpand = () => {
        setExpanded((x) => !x);
        if (!a.read) onRead();
    };

    const copy = async (txt, tag, toneUsed) => {
        try {
            await navigator.clipboard.writeText(txt);
            setCopied(tag);
            setTimeout(() => setCopied(""), 1800);
            if (toneUsed && tag === "all") {
                api.post(`/alerts/${a.id}/pick-variant`, { tone: toneUsed }).then(onRefresh).catch(() => {});
            }
        } catch { /* clipboard blocked */ }
    };

    // Open default email client with edited subject + body prefilled via mailto:
    const openInMailClient = () => {
        const subj = encodeURIComponent(editedSubject);
        const body = encodeURIComponent(editedBody);
        window.open(`mailto:?subject=${subj}&body=${body}`, "_blank");
        // Record the pick so it counts toward A/B win-rate
        api.post(`/alerts/${a.id}/pick-variant`, { tone: activeTone }).then(onRefresh).catch(() => {});
    };

    const regenerate = async () => {
        setRegenerating(true);
        try {
            await api.post(`/alerts/${a.id}/regenerate-email`);
            // Reset decision since the variants will change
            setDecision("undecided");
            setEditedSubject("");
            setEditedBody("");
            setTimeout(onRefresh, 5000);
            setTimeout(onRefresh, 15000);
            setTimeout(onRefresh, 30000);
        } catch { /* ignore */ } finally {
            setRegenerating(false);
        }
    };

    return (
        <div
            data-testid={`alert-${a.id}`}
            className={`border-b border-zinc-800/60 ${a.read ? "" : "bg-zinc-900/20"}`}
        >
            <button
                onClick={onExpand}
                data-testid={`reopen-card-toggle-${a.id}`}
                className="w-full text-left px-4 py-3 hover:bg-zinc-900/40"
            >
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400 mb-0.5">
                            🔁 Re-opened · {isFirm ? "Firm dossier" : "Pitch"}
                        </div>
                        <div className="text-sm text-zinc-50 font-semibold truncate">{a.label}</div>
                        <div className="text-[10px] text-zinc-500 font-mono mt-0.5">
                            A prospect re-visited this share link · {relTime(a.created_at)}
                            {chosen && <span className="text-amber-400 ml-1">· you picked {chosen}</span>}
                        </div>
                        {a.slider_intent?.top_slider && (
                            <div className="mt-1 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-amber-300 border border-amber-500/40 bg-amber-500/5 px-1.5 py-0.5" data-testid={`slider-intent-${a.id}`}>
                                📊 focused on: {a.slider_intent.top_slider.replace(/_/g, " ")} ({a.slider_intent.top_slider_drag_count}× drags)
                            </div>
                        )}
                    </div>
                </div>
                <div className="mt-1.5 inline-flex items-center gap-2 text-[9px] font-mono uppercase tracking-wider text-zinc-600">
                    <span className="hover:text-emerald-400">{expanded ? "Hide" : "Show"} follow-up email</span>
                    <ArrowRight className={`w-2.5 h-2.5 transition-transform ${expanded ? "rotate-90" : ""}`} />
                </div>
            </button>

            {expanded && (
                <div className="px-4 pb-3 -mt-1" data-testid={`reopen-card-body-${a.id}`}>
                    <a
                        href={a.url || "#"}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`reopen-open-link-${a.id}`}
                        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-emerald-400 mb-2"
                    >
                        <ExternalLink className="w-2.5 h-2.5" /> Open the shared link
                    </a>

                    <div className="border border-zinc-800 bg-black p-3" data-testid={`reopen-email-${a.id}`}>
                        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-amber-400 flex items-center gap-1">
                                <Mail className="w-3 h-3" /> AI follow-up · A/B variants
                            </div>
                            <button
                                onClick={regenerate}
                                disabled={regenerating || status === "drafting"}
                                data-testid={`regen-email-${a.id}`}
                                className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-zinc-500 hover:text-amber-400 border border-zinc-800 px-1.5 py-0.5 disabled:opacity-50"
                            >
                                <RefreshCw className={`w-2.5 h-2.5 ${regenerating ? "animate-spin" : ""}`} /> Regen
                            </button>
                        </div>

                        {status === "drafting" && (
                            <div className="font-mono text-[10px] text-zinc-500" data-testid={`reopen-email-drafting-${a.id}`}>
                                Drafting 2 variants (casual + direct) in parallel with Claude… (~15-25s)
                            </div>
                        )}

                        {status === "error" && (
                            <div className="font-mono text-[10px] text-red-400" data-testid={`reopen-email-error-${a.id}`}>
                                Couldn't generate email: {a.email_error || "unknown error"}. Try Regen.
                            </div>
                        )}

                        {status === "ready" && variant && (
                            <div className="space-y-2">
                                {/* Variant tabs */}
                                <div className="flex items-center gap-1 mb-1" data-testid={`variant-tabs-${a.id}`}>
                                    {variants.map((v) => (
                                        <button
                                            key={v.tone}
                                            onClick={() => onSwitchTone(v.tone)}
                                            data-testid={`variant-tab-${v.tone}-${a.id}`}
                                            className={`text-[9px] font-mono uppercase tracking-wider px-2 py-1 border transition-colors ${
                                                activeTone === v.tone
                                                    ? "border-amber-500/60 bg-amber-500/10 text-amber-300"
                                                    : "border-zinc-800 text-zinc-500 hover:text-zinc-300"
                                            }`}
                                        >
                                            {v.tone}{chosen === v.tone && <span className="ml-1 text-amber-400">✓</span>}
                                        </button>
                                    ))}
                                </div>

                                {/* Preview (when undecided or skipped) */}
                                {decision !== "yes" && (
                                    <>
                                        <div>
                                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-0.5">Subject</div>
                                            <div className="flex items-start gap-2">
                                                <div className="text-sm text-zinc-100 font-semibold leading-snug flex-1" data-testid={`reopen-email-subject-${a.id}`}>{variant.subject}</div>
                                                <button
                                                    onClick={() => copy(variant.subject, "subject", null)}
                                                    data-testid={`copy-subject-${a.id}`}
                                                    className="shrink-0 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-zinc-500 hover:text-emerald-400 border border-zinc-800 px-1.5 py-0.5"
                                                >
                                                    <Copy className="w-2.5 h-2.5" /> {copied === "subject" ? "✓" : "Copy"}
                                                </button>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-0.5">Body</div>
                                            <div className="text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed" data-testid={`reopen-email-body-${a.id}`}>{variant.body}</div>
                                        </div>
                                    </>
                                )}

                                {/* Decision gate: ask the user if they want to send */}
                                {decision === "undecided" && (
                                    <div className="border-t border-zinc-800 pt-3 mt-3" data-testid={`decide-${a.id}`}>
                                        <div className="font-mono text-[10px] text-zinc-400 mb-2">Want to send this follow-up?</div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <button
                                                onClick={onYes}
                                                data-testid={`decide-yes-${a.id}`}
                                                className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-[10px] uppercase tracking-wider px-3 py-1.5"
                                            >
                                                <Mail className="w-3 h-3" /> Yes, edit &amp; send
                                            </button>
                                            <button
                                                onClick={onNo}
                                                data-testid={`decide-no-${a.id}`}
                                                className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-zinc-600 text-zinc-400 hover:text-zinc-200 text-[10px] uppercase tracking-wider font-mono px-3 py-1.5"
                                            >
                                                No, skip
                                            </button>
                                            <button
                                                onClick={() => copy(`Subject: ${variant.subject}\n\n${variant.body}`, "all", variant.tone)}
                                                data-testid={`copy-all-${a.id}`}
                                                title="Just copy without sending — feeds the win-rate stats."
                                                className="ml-auto inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-amber-300 hover:text-amber-200 border border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10 px-2 py-1"
                                            >
                                                <Copy className="w-2.5 h-2.5" /> {copied === "all" ? `Picked ${variant.tone} ✓` : `Copy only`}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Skipped */}
                                {decision === "no" && (
                                    <div className="border-t border-zinc-800 pt-3 mt-3 flex items-center justify-between gap-2" data-testid={`skipped-${a.id}`}>
                                        <span className="font-mono text-[10px] text-zinc-500">Skipped — no follow-up sent.</span>
                                        <button
                                            onClick={() => setDecision("undecided")}
                                            data-testid={`undo-skip-${a.id}`}
                                            className="text-[9px] font-mono uppercase tracking-wider text-zinc-500 hover:text-emerald-400"
                                        >
                                            Undo
                                        </button>
                                    </div>
                                )}

                                {/* Edit + send mode */}
                                {decision === "yes" && (
                                    <div className="border-t border-emerald-500/30 pt-3 mt-3 space-y-2" data-testid={`edit-mode-${a.id}`}>
                                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-emerald-400">Edit before sending · {activeTone}</div>
                                        <div>
                                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-0.5">Subject</div>
                                            <input
                                                value={editedSubject}
                                                onChange={(e) => setEditedSubject(e.target.value)}
                                                data-testid={`edit-subject-${a.id}`}
                                                className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 outline-none text-sm text-zinc-100 px-2 py-1.5 font-semibold"
                                            />
                                        </div>
                                        <div>
                                            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-0.5">Body</div>
                                            <textarea
                                                value={editedBody}
                                                onChange={(e) => setEditedBody(e.target.value)}
                                                rows={8}
                                                data-testid={`edit-body-${a.id}`}
                                                className="w-full bg-zinc-950 border border-zinc-800 focus:border-emerald-500 outline-none text-xs text-zinc-200 px-2 py-1.5 leading-relaxed font-mono resize-y"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap pt-1">
                                            <button
                                                onClick={openInMailClient}
                                                disabled={!editedSubject.trim() || !editedBody.trim()}
                                                data-testid={`send-email-${a.id}`}
                                                className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-bold text-[10px] uppercase tracking-wider px-3 py-1.5"
                                            >
                                                <Mail className="w-3 h-3" /> Open in email client
                                            </button>
                                            <button
                                                onClick={() => copy(`Subject: ${editedSubject}\n\n${editedBody}`, "all", activeTone)}
                                                data-testid={`copy-edited-${a.id}`}
                                                className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-zinc-300 hover:text-amber-300 border border-zinc-800 hover:border-amber-500/40 px-2 py-1"
                                            >
                                                <Copy className="w-2.5 h-2.5" /> {copied === "all" ? "Copied!" : "Copy edited"}
                                            </button>
                                            <button
                                                onClick={() => seedEditor(variant)}
                                                data-testid={`reset-edits-${a.id}`}
                                                title="Restore Claude's original draft"
                                                className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-zinc-500 hover:text-zinc-200 border border-zinc-800 px-2 py-1"
                                            >
                                                Reset
                                            </button>
                                            <button
                                                onClick={() => setDecision("undecided")}
                                                data-testid={`cancel-edit-${a.id}`}
                                                className="ml-auto text-[9px] font-mono uppercase tracking-wider text-zinc-500 hover:text-red-400"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function relTime(iso) {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    if (isNaN(t)) return "";
    const d = (Date.now() - t) / 1000;
    if (d < 60) return `${Math.max(1, Math.floor(d))}s ago`;
    if (d < 3600) return `${Math.floor(d / 60)}m ago`;
    if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
    return `${Math.floor(d / 86400)}d ago`;
}

